import { Controller } from "@hotwired/stimulus"
import { apiJson, formatApiErrorDetail } from "../lib/api_errore"
import { conservaDalMorph } from "../lib/conserva_morph"

// L'assistente EROGO (AiutoFase.js, 31a5bd1). La conversazione vive solo qui, in memoria: una visita Turbo rende di
// nuovo il layout, il controller riparte e la conversazione è vuota (l'originale la azzera al cambio di pagina).
// Il contatore `richiesta` scarta le risposte arrivate dopo una domanda più nuova o dopo il cambio di pagina.
// Indietro/Avanti: Turbo rimette la fotografia della pagina dalla cache, con i messaggi di allora; connect() la svuota,
// così quello che si vede è quello che parte in `cronologia` (vuota), come l'originale al cambio di pathname/search.
// Il testo dell'assistente va nel DOM come testo (textContent), mai come HTML; i link delle azioni solo interni o http(s).
// Differenza voluta (docs/PARITA.md, piano 03, task 61): un'azione con un altro url (javascript:, data:, //host) è resa
// come testo semplice, senza link e senza sottolineatura; l'originale la rendeva comunque come <Link>.
const MESSAGGIO = "rounded border p-3 text-sm"
const DA_TE = "bg-zinc-50"
const DALL_ASSISTENTE = "bg-amber-50 border-amber-200"

export function urlSicuro(url) {
  if (typeof url !== "string" || url === "") return null
  if (url.startsWith("/") && !url.startsWith("//") && !url.startsWith("/\\")) return url
  try {
    const u = new URL(url)
    return u.protocol === "http:" || u.protocol === "https:" ? u.href : null
  } catch {
    return null
  }
}

export default class extends Controller {
  static targets = ["messaggi", "domanda", "invio", "rapida", "attesa", "errore"]
  static values = { fase: String, projectId: String }

  connect() {
    this.richiesta = 0
    this.messaggi = []
    this.busy = false
    this.messaggiTarget.replaceChildren()
    this.domandaTarget.value = ""
    this.mostraErrore("")
    this.occupato(false)
  }

  disconnect() {
    this.richiesta += 1
  }

  // Un refresh con morph (stessa pagina) non tocca il pannello: la conversazione aperta resta. Nota: oggi le pagine con
  // morph (verifica/show, bandi/index) non fanno visite «replace» con un'altra query; se ne arrivasse una, il morph
  // terrebbe la conversazione, mentre l'originale la azzera quando cambia `search` (revisione task 61, M8).
  conserva(event) {
    conservaDalMorph(this.element, event)
  }

  aggiorna() {
    this.invioTarget.disabled = this.busy || !this.domandaTarget.value.trim()
  }

  chiedi(event) {
    this.manda(event.params.domanda)
  }

  invia(event) {
    event.preventDefault()
    this.manda(this.domandaTarget.value)
  }

  // «Percorso in breve»: chiude l'assistente e apre la guida di benvenuto dalla prima schermata.
  percorso() {
    window.dispatchEvent(new CustomEvent("dialog:close", { detail: { id: "aiuto-fase" } }))
    window.dispatchEvent(new CustomEvent("guida-benvenuto:apri"))
  }

  async manda(testo) {
    if (this.busy || !testo || !testo.trim()) return
    const n = ++this.richiesta
    this.occupato(true)
    this.mostraErrore("")
    const cronologia = this.messaggi.slice(-6).map(({ ruolo, testo }) => ({ ruolo, testo: testo.slice(0, 1500) }))
    this.aggiungi({ ruolo: "user", testo })
    this.domandaTarget.value = ""
    const corpo = { domanda: testo, fase: this.faseValue, project_id: this.projectIdValue || null, cronologia }
    try {
      const data = await apiJson("/api/assistente/domanda", { method: "POST", body: corpo })
      if (this.richiesta === n) {
        this.aggiungi({ ruolo: "assistant", testo: data.risposta, azioni: data.azioni, nota: data.nota, origine: data.origine, guida: data.guida })
      }
    } catch (e) {
      if (this.richiesta === n) {
        this.mostraErrore(formatApiErrorDetail(e.detail) || "Assistente non disponibile: riprova.")
        this.domandaTarget.value = testo
      }
    } finally {
      if (this.richiesta === n) this.occupato(false)
    }
  }

  occupato(si) {
    this.busy = si
    this.attesaTarget.hidden = !si
    this.rapidaTargets.forEach((b) => { b.disabled = si })
    this.aggiorna()
  }

  mostraErrore(testo) {
    this.erroreTarget.textContent = testo
    this.erroreTarget.hidden = !testo
  }

  aggiungi(m) {
    this.messaggi.push(m)
    const box = document.createElement("div")
    box.className = `${MESSAGGIO} ${m.ruolo === "user" ? DA_TE : DALL_ASSISTENTE}`
    const chi = document.createElement("b")
    chi.textContent = m.ruolo === "user" ? "Tu" : (m.origine === "programma" ? "Assistente · guida del programma" : "Assistente · proposta AI")
    const p = document.createElement("p")
    p.className = "whitespace-pre-wrap mt-1"
    p.textContent = m.testo ?? ""
    box.append(chi, p)
    ;(Array.isArray(m.azioni) ? m.azioni : []).forEach((a) => {
      const url = urlSicuro(a?.url)
      const el = document.createElement(url ? "a" : "span")
      el.className = "block underline mt-2"
      el.textContent = a?.testo ?? ""
      if (url) {
        el.href = url
        el.dataset.action = "click->dialog#close"
      } else {
        el.className = "block mt-2"
      }
      box.append(el)
    })
    if (m.nota) {
      const nota = document.createElement("p")
      nota.className = "text-xs text-zinc-500 mt-2"
      nota.textContent = m.nota
      box.append(nota)
    }
    if (m.guida) {
      const g = document.createElement("p")
      g.className = "text-xs text-zinc-500 mt-1"
      const titoli = (Array.isArray(m.guida.sezioni) ? m.guida.sezioni : []).map((s) => s?.titolo).join("; ")
      g.textContent = `Guida operativa ${m.guida.versione} · ${titoli}`
      box.append(g)
    }
    this.messaggiTarget.append(box)
  }
}
